import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { useNavigate } from "react-router-dom";
import ProjectTable from "./Components/ProjectTable";
import { dataService } from "../../../Services/data/data.service";
import { Fab, Divider, Tooltip, LinearProgress, IconButton, Typography, Box, Grid2 } from "@mui/material";
import { ChartNoAxesColumn, Plus, Infinity, List, LayoutGrid, EllipsisVertical, Pencil, Search, ReceiptEuro, PencilRuler } from "lucide-react";
import ResetFiltersButton from "../../Filter/ResetFiltersButton";
import { Spin } from "react-cssfx-loading";
import { toast } from "react-toastify";
import { ProjectFilter, TableView, ProjectFilterTypes } from "../../../Config/constants";
import DateRangePicker from "../../Shared/DateSelection/DateRangePicker";
import QrButton from "../../Shared/QrButton";
import BackToTopButton from "../../Shared/Buttons/BackToTopButton";
import Button from "../../Shared/Buttons/Button";
import HeaderBar from "../../Shared/Header/HeaderBar";
import HeaderTitle from "../../Shared/Header/HeaderTitle";
import HeaderEnd from "../../Shared/Header/HeaderEnd";
import useFilterPersistence from "../../../hooks/useFilterPersistence";

function ProjectReportOverviewPage(props) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [projectList, setProjectList] = useState([]);
  const [originalProjectList, setOriginalProjectList] = useState([]); // Should never be overwritten
  const initialProjectList = useRef([]);
  const firstLoad = useRef(false);
  const [loadAmount, setLoadAmount] = useState(0);
  const currentDate = new Date();
  const [loading, setLoading] = useState(true);
  const [tableViewMode, setTableViewMode] = useState(TableView.GRID);

  // ── Persistent filter state ────────────────────────────────────────────────
  // URL is the canonical source of truth; localStorage is the fallback.
  const {
    filters,
    setFilterText,
    resetFilters,
    getFilterValue,
    hasActiveFilters,
    extraParams,
    setExtraParam,
  } = useFilterPersistence({
    pageKey: 'project-report-overview',
    filterSchema: {
      general:  ProjectFilterTypes.GENERAL,
      customer: ProjectFilterTypes.CUSTOMER,
      project:  ProjectFilterTypes.PROJECT_NAME,
      location: ProjectFilterTypes.LOCATION_NAME,
    },
    defaultFilters: [],
    extraParamDefaults: {
      toggle:    ProjectFilter.ALLPROJECTS,
      startDate: '',
      endDate:   '',
    },
  });

  // Derive convenience values from extraParams
  const visibleProjectFilter = extraParams.toggle;
  const startDate = extraParams.startDate ? new Date(extraParams.startDate) : null;
  const endDate   = extraParams.endDate   ? new Date(extraParams.endDate)   : null;
  const filteringDateRange = Boolean(startDate && endDate);

  // Filtering UI toggle (not persisted — transient UI state)
  const [filterOn, setFilterOn] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(8);
  const [types, setTypes] = useState([]);
  // Dropdown
  const [dropdownItem, setDropdownItem] = useState(null);
  const dropdownRef = useRef(null);

  // Subscription
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [validSubscription, setValidSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [projectCount, setProjectCount] = useState(0);

  // General
  const [generalPageError, setGeneralPageError] = useState(null);

  // const loadMore = () => {
  //     if (window.innerHeight + document.documentElement.scrollTop === document.scrollingElement.scrollHeight) {
  //         let tempLoadAmount = loadAmount + 12;
  //         if (firstLoad.current) {
  //             dataService.getAllUserCompanyProjects(props.Login.userid, loadAmount, 12).then(results => {

  //                 setProjectList(sortProjectsOnFinished(initialProjectList.current.concat(results)));
  //                 setOriginalProjectList(sortProjectsOnFinished(initialProjectList.current.concat(results)))
  //             });
  //             firstLoad.current = false;
  //         }
  //         else {
  //             dataService.getAllUserCompanyProjects(props.Login.userid, loadAmount, 12).then(results => {
  //                 if (results.length > 0) {
  //                     setProjectList(sortProjectsOnFinished(projectList.concat(results)));
  //                     setOriginalProjectList(sortProjectsOnFinished(projectList.concat(results)))
  //                 }
  //             });
  //         }
  //         setLoadAmount(tempLoadAmount);
  //         window.removeEventListener('scroll', loadMore);
  //     }
  // }

  useEffect(() => {
    dataService.getTypes().then((results) => {
      if (results) {
        setTypes(results.sort((a, b) => a.description.localeCompare(b.description)));
      }
    });
  }, []);

  // Re-apply filters when filters or date range changes (covers reset via hook)
  useEffect(() => {
    filterProjects(filters);
  }, [filters, filteringDateRange, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.Login.subscriberUniqueId !== null) {
      setUserIsAdmin(true);

      // get subscription information
      // get subscription information
      dataService
        .getSubscriptionBySubscriberUniqueId(props.Login.subscriberUniqueId)
        .then((result) => {
          if (result.success) {
            setSubscription(result.subscription);
            setValidSubscription(true);
          } else {
            setGeneralPageError("You don't have a valid subscription. This means you are not able to create companies or users.");
            setValidSubscription(false);
          }
        })
        .catch((error) => {
          toast.error(`You don't have a valid subscription`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        });
    }
    dataService
      .getUserCompanyProjectOverview(props.Login.userid)
      .then((results) => {
        var elements = sortProjectsOnFinished(results);

        let activeProjects = elements.filter((project) => project.isActive && new Date(project.timeRangeBegin) <= currentDate && new Date(project.timeRangeEnd) >= currentDate);
        let projectCount = new Set(activeProjects.map((project) => project.projectName)).size;
        setProjectCount(projectCount);

        // setProjectList(elements);
        filterProjects(filters, elements);
        setOriginalProjectList(elements);

        initialProjectList.current = results;
        firstLoad.current = true;
      })
      .catch((error) => {
        toast.error(`A problem occured while fetching the data`, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        console.log("Error: ", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const ResultRedirect = (projectQr) => {
    props.actions.projectReportAction(true);
    navigate(`/results/${projectQr}`);
  };

  const ModifyRedirect = (projectId) => {
    let thisProject = projectList.find((x) => x.id === projectId);
    let fromDate = thisProject.timeRangeBegin;
    let toDate = thisProject.timeRangeEnd;
    props.actions.fromDateAction(fromDate);
    props.actions.toDateAction(toDate);
    navigate(`/modifyproject/${thisProject.qrCode}`);
  };

  const ReportRedirect = (projectId) => {
    let thisProject = projectList.find((x) => x.id === projectId);
    navigate(`/projectreport/${thisProject.qrCode}`);
  };

  const getPercentage = (fromDate, toDate) => {
    let fromDateDate = new Date(fromDate);
    let toDateDate = new Date(toDate);
    let difference = currentDate.getTime() - fromDateDate.getTime();
    let percentage = (difference / (toDateDate.getTime() - fromDateDate.getTime())) * 100;
    return percentage;
  };

  const getDaysLeft = (fromDate, toDate) => {
    let toDateDate = new Date(toDate);
    let difference = toDateDate.getTime() - currentDate.getTime();
    let daysLeft = Math.round(difference / (1000 * 60 * 60 * 24));
    return daysLeft;
  };

  const getMeasurementCount = (project) => {
    let measurementCount = project.measurementCount;

    if (project.measurementCount === 0) {
      return "No events recorded";
    } else {
      return measurementCount;
    }
  };

  const navigateTo = (destination) => {
    navigate(destination, { state: { from: "projectreportoverview" } });
  };

  const sortProjectsOnFinished = (projects) => {
    var results = projects.map((project) => {
      let percentage = getPercentage(Date.parse(project.timeRangeBegin), Date.parse(project.timeRangeEnd));
      if (percentage < 100) {
        project.finished = false;
      } else {
        project.finished = true;
      }

      return project;
    });

    results = results.sort((a, b) => a.finished - b.finished);
    return results;
  };

  const changeVisibleProjects = (value) => {
    // Persist the toggle in URL + localStorage
    setExtraParam('toggle', value);
  };

  const handleTableView = (view) => {
    setTableViewMode(view);
  };

  // Counts the amount of measurements made in a project
  const getEventsCount = (projectDetails) => {
    let measurementCount = 0;

    projectDetails.forEach((detail) => {
      measurementCount += detail.measurements.length;
    });

    return measurementCount;
  };

  const handleToggleDropdown = (id) => {
    if (dropdownItem) {
      setDropdownItem(null);
      return;
    }
    setDropdownItem(id);
  };

  const handleClickOutsideDropdown = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setTimeout(() => {
        setDropdownItem(null);
      }, 100);
    }
  };

  const datesAreOnSameDay = (first, second) => {
    if (first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate()) {
      return true;
    } else {
      return false;
    }
  };

  const dateRange = (a_start, a_end, b_start, b_end) => {
    if (a_start <= b_start && b_start <= a_end) return true;
    if (a_start <= b_end && b_end <= a_end) return true;
    if (b_start < a_start && a_end < b_end) return true;
    if (datesAreOnSameDay(a_start, b_start)) return true;
    if (datesAreOnSameDay(b_end, a_end)) return true;
    return false;
  };

  // Date range helpers used by DateRangePicker component
  const setDateRange = (sd, ed) => {
    const sdIso = sd ? new Date(sd).toISOString() : '';
    const edIso = ed ? new Date(ed).toISOString() : '';
    setExtraParam('startDate', sdIso);
    setExtraParam('endDate', edIso);
  };
  const handleSetStartDate = (event) => { setDateRange(event, endDate); };
  const handleSetEndDate   = (event) => { setDateRange(startDate, event); };
  const resetDateRangeFilter = ()     => { setDateRange(null, null); };

  useEffect(() => {
    if (dropdownItem) {
      document.addEventListener("mousedown", handleClickOutsideDropdown);
    } else {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
    };
  }, [dropdownItem]);

  const filter = [
    { number: 1, column: "companyName", sortOrder: "ASC" },
    { number: 2, column: "companyName", sortOrder: "DESC" },
    { number: 3, column: "projectName", sortOrder: "ASC" },
    { number: 4, column: "projectName", sortOrder: "DESC" },
    { number: 5, column: "resourceDescription", sortOrder: "ASC" },
    { number: 6, column: "resourceDescription", sortOrder: "DESC" },
    { number: 7, column: "timeRangeBegin", sortOrder: "ASC" },
    { number: 8, column: "timeRangeBegin", sortOrder: "DESC" },
    { number: 9, column: "eventCount", sortOrder: "ASC" },
    { number: 9, column: "eventCount", sortOrder: "DESC" },
  ];

  const dynamicSorting = (property) => {
    var sortOrder = 1;
    if (property[0] === "-") {
      sortOrder = -1;
      property = property.substr(1);
    }
    let asc = false;

    /* SC: Filter now works both ways (ASC, DESC) */
    if (filter.find((x) => x.number === selectedFilter).column === property) {
      if (filter.find((x) => x.number === selectedFilter).sortOrder === "ASC") {
        asc = false;
        setSelectedFilter(filter.find((x) => x.column === property && x.sortOrder === "DESC").number);
      } else {
        asc = true;
        setSelectedFilter(filter.find((x) => x.column === property && x.sortOrder === "ASC").number);
      }
    } else {
      asc = true;
      setSelectedFilter(filter.find((x) => x.column === property && x.sortOrder === "ASC").number);
    }
    if (asc === true) {
      if (property === "eventCount") {
        return function (a, b) {
          var result = getEventsCount(a.details) > getEventsCount(b.details) ? 1 : -1;
          return result * sortOrder;
        };
      } else {
        return function (a, b) {
          var result = a[property].toLowerCase() > b[property].toLowerCase() ? 1 : -1;
          return result * sortOrder;
        };
      }
    } else {
      if (property === "eventCount") {
        return function (a, b) {
          var result = getEventsCount(a.details) < getEventsCount(b.details) ? 1 : -1;
          return result * sortOrder;
        };
      } else {
        return function (a, b) {
          var result = a[property].toLowerCase() < b[property].toLowerCase() ? 1 : -1;
          return result * sortOrder;
        };
      }
    }
  };

  const handleSorting = (sortType) => {
    let tempProjectList = [...projectList];
    setProjectList(tempProjectList.sort(dynamicSorting(sortType)));
  };

  const toggleFilter = () => {
    if (filterOn) {
      resetFilters();
    }
    setFilterOn(!filterOn);
  };

  const updateFilters = (type, event) => {
    // Text filters are debounced so the URL isn't updated on every keystroke.
    // The local state (and therefore the visible list) updates immediately.
    setFilterText(type, event.target.value);
    filterProjects(
      [{ type, filterValue: event.target.value }, ...filters.filter(f => f.type !== type)]
    );
  };

  const filterProjects = (thisFilters, projectList) => {
    let varFilteredProjects = projectList ? [...projectList] : [...originalProjectList];
    if (filteringDateRange) {
      varFilteredProjects = varFilteredProjects.filter((x) => new Date(x.timeRangeBegin) > new Date(startDate) && new Date(x.timeRangeEnd) < new Date(endDate));
    }

    for (let i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case ProjectFilterTypes.GENERAL:
          varFilteredProjects = varFilteredProjects.filter(
            (x) =>
              x.companyName.toLowerCase().includes(filter.filterValue.toLowerCase()) ||
              x.projectName.toLowerCase().includes(filter.filterValue.toLowerCase()) ||
              x.resourceDescription.toLowerCase().includes(filter.filterValue.toLowerCase())
          );
          break;
        case ProjectFilterTypes.CUSTOMER:
          varFilteredProjects = varFilteredProjects.filter((x) => x.companyName.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case ProjectFilterTypes.PROJECT_NAME:
          varFilteredProjects = varFilteredProjects.filter((x) => x.projectName.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case ProjectFilterTypes.LOCATION_NAME:
          varFilteredProjects = varFilteredProjects.filter((x) => x.resourceDescription.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        default:
          varFilteredProjects = varFilteredProjects.filter((x) => x.companyName.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
      }
    }
    // setProjects(varFilteredProjects)
    setProjectList(varFilteredProjects);
  };

  const getSubscriptionProjectLimit = () => {
    if (subscription.projectLimit) {
      return subscription.projectLimit;
    } else {
      return <Infinity className={classes.infinityIcon} />;
    }
  };

  return (
    <Box>
      {loading && (
        <div className={classes.loadingChart}>
          <Spin width="100px" height="100px" color="var(--color-primary)"></Spin>
        </div>
      )}
      <Box className="page-container">
        <HeaderBar>
          <HeaderTitle>Project overview</HeaderTitle>
          {subscription && (
            <HeaderEnd>
              <div className={classes.subscriptionProjectCountChip}>
                <PencilRuler className={classes.chip__icons} />
                <p className={classes.chip__text}>
                  {projectCount}/{getSubscriptionProjectLimit()}
                </p>
              </div>
            </HeaderEnd>
          )}
        </HeaderBar>
        {/* Filter row */}
        <div className={classes.tableManagementContainer}>
          <div className={classes.tableManagementContainer__start}>
            <div className={classes.searchContainer}>
              <input
                placeholder="Search for projects"
                className={classes.search__input}
                type="text"
                value={getFilterValue(ProjectFilterTypes.GENERAL)}
                onChange={(e) => updateFilters(ProjectFilterTypes.GENERAL, e)}
              />
              <Search size={16} className={classes.inputIcon} />
            </div>

            <DateRangePicker
              setDateRange={setDateRange}
              startDate={startDate}
              endDate={endDate}
              resetDateRangeFilter={resetDateRangeFilter}
              handleSetStartDate={handleSetStartDate}
              handleSetEndDate={handleSetEndDate}
            />

            <div className={classes.toggleFilter}>
              <button onClick={() => changeVisibleProjects(ProjectFilter.ALLPROJECTS)} style={{ width: "144px" }} className={classes.toggleFilter__optionButton}>
                <p className={classes.toggleFilter__label}>Company projects</p>
              </button>
              <button onClick={() => changeVisibleProjects(ProjectFilter.MYPROJECTS)} style={{ width: "144px" }} className={classes.toggleFilter__optionButton}>
                <p className={classes.toggleFilter__label}>My projects</p>
              </button>
              <div style={{ transform: visibleProjectFilter === ProjectFilter.ALLPROJECTS ? "translateX(0px)" : "translateX(144px)" }} className={classes.toggleSelected} />
            </div>
            {/* Reset filters — visible whenever any filter, date range, or toggle is active */}
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
          </div>
          <div className={classes.tableManagementContainer__end}>
            <div className={classes.toggleFilter}>
              <button onClick={() => handleTableView(TableView.LIST)} style={{ width: "64px" }} className={classes.toggleFilter__optionButton}>
                <List className={classes.toggleFilter__icon} />
                <p className={classes.toggleFilter__label}>List</p>
              </button>
              <button onClick={() => handleTableView(TableView.GRID)} style={{ width: "64px" }} className={classes.toggleFilter__optionButton}>
                <LayoutGrid className={classes.toggleFilter__icon} />
                <p className={classes.toggleFilter__label}>Grid</p>
              </button>
              <div style={{ transform: tableViewMode === TableView.LIST ? "translateX(0px)" : "translateX(64px)" }} className={classes.toggleSelected} />
            </div>

            <Button size="small" variant="primary" startIcon={<Plus />} onClick={() => navigateTo("/create")}>
              Create project
            </Button>
          </div>
        </div>
        {tableViewMode === TableView.GRID ? (
          <>
            {/* Grid */}
            <Grid2 container spacing={2}>
              {projectList.filter((project) => (visibleProjectFilter === ProjectFilter.MYPROJECTS ? project.insertedByUserId === props.Login.userid : project.isActive)).length === 0 && (
                <Grid2
                  size={{ xs: 12, md: 6, lg: 4, xl: 3 }}
                  className={classes.boxStyle}
                  sx={{ background: "rgb(195, 153, 107, 0.15)" }}
                  onClick={() => navigateTo("/create")}
                  key={"createNewGraphBlock"}
                >
                  <Box sx={{ border: "2px dashed var(--color-primary)", height: "100%", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: "8px" }}>
                    <Plus size="36" style={{ color: "var(--color-primary)", fontSize: "30px" }}></Plus>
                    <Typography variant="h6" color="var(--color-primary)">
                      Create a new project
                    </Typography>
                  </Box>
                </Grid2>
              )}
              {projectList
                .filter((project) => (visibleProjectFilter === ProjectFilter.MYPROJECTS ? project.insertedByUserId === props.Login.userid : project.isActive))
                .map((project) => (
                  <Grid2 size={{ xs: 12, md: 6, lg: 4, xl: 3 }} className={classes.boxStyle} key={project.id}>
                    <div className={classes.headerContainer}>
                      <Typography variant="h6" className={classes.headerTitle}>
                        {project.projectName}
                      </Typography>
                      <div className={classes.buttonContainer}>
                        {project.isCompanyActive ? (
                          <>
                            <QrButton
                              borderRadius={4}
                              disabled={!dateRange(new Date(project.timeRangeBegin), new Date(project.timeRangeEnd), new Date(currentDate), new Date(currentDate))}
                              projectName={project.projectName}
                              resourceName={project.resourceName}
                              QrCode={project.qrCode}
                              size="large"
                            />
                            <IconButton onClick={() => navigate(`/modifyproject/${project.qrCode}`)} size="small" style={{ height: "50px", width: "50px", borderRadius: "4px" }}>
                              <Pencil />
                            </IconButton>
                          </>
                        ) : (
                          <Tooltip title="Company is not active" placement="top" disableInteractive>
                            <div style={{ backgroundColor: "#EDEDED", height: "24px", padding: "0px 8px", borderRadius: "12px", display: "flex", alignItems: "center" }}>
                              <p style={{ fontSize: "14px" }}>View only</p>
                            </div>
                          </Tooltip>
                        )}
                        <button
                          onClick={() => handleToggleDropdown(project.id)}
                          className={classes.openDropdownButton}
                          style={{ backgroundColor: dropdownItem === project.id ? "#EDEDED" : "transparent" }}
                        >
                          <EllipsisVertical size={20} absoluteStrokeWidth strokeWidth={1.4} />
                        </button>
                        {dropdownItem === project.id ? (
                          <div ref={dropdownRef} className={classes.row__dropdown}>
                            <button className={classes.row__dropdownButton} onClick={() => navigate(`/results/${project.qrCode}`)}>
                              <ChartNoAxesColumn className={classes.iconButton__icon} />
                              Project timeline
                            </button>
                            {/* <button className={classes.row__dropdownButton} onClick={() => navigate(`/projectreport/${project.qrCode}`)}>
                                                    <ReceiptEuro className={classes.iconButton__icon} />
                                                    Financial dashboard
                                                </button> */}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <Divider></Divider>
                    <div className={classes.dateContainer}>
                      <p className={classes.dataContainer__label}>Time range</p>
                      {!project.finished ? (
                        <Tooltip
                          title={
                            getDaysLeft(Date.parse(project.timeRangeBegin), Date.parse(project.timeRangeEnd)) === 1
                              ? `${getDaysLeft(Date.parse(project.timeRangeBegin), Date.parse(project.timeRangeEnd))} day left`
                              : `${getDaysLeft(Date.parse(project.timeRangeBegin), Date.parse(project.timeRangeEnd))} days left`
                          }
                        >
                          <LinearProgress
                            className={classes.bar}
                            style={{ height: "8px", borderRadius: "4px" }}
                            color="success"
                            variant="determinate"
                            value={getPercentage(Date.parse(project.timeRangeBegin), Date.parse(project.timeRangeEnd))}
                          />
                        </Tooltip>
                      ) : (
                        <LinearProgress className={classes.bar} style={{ height: "8px", borderRadius: "4px" }} color="error" variant="determinate" value={100} />
                      )}
                    </div>
                    <div className={classes.dataContainer}>
                      <p className={classes.dataContainer__label}>Company</p>
                      <Typography variant="h7">{" " + (project.companyName !== null && project.companyName !== undefined ? project.companyName : "-")}</Typography>
                    </div>
                    <div className={classes.dataContainer}>
                      <p className={classes.dataContainer__label}>Registration point</p>
                      <Typography variant="h7">{" " + project.resourceDescription}</Typography>
                    </div>
                    <div className={classes.dataContainer}>
                      <p className={classes.dataContainer__label}>Events</p>
                      <Typography variant="h7">{" " + getMeasurementCount(project)}</Typography>
                    </div>
                  </Grid2>
                ))}
            </Grid2>
          </>
        ) : (
          <Grid2 item size={12}>
            <ProjectTable
              toggleFilter={toggleFilter}
              filters={filters}
              handleSorting={handleSorting}
              filterOn={filterOn}
              filterProjects={filterProjects}
              updateFilters={updateFilters}
              types={types}
              rows={projectList.filter((project) => (visibleProjectFilter === ProjectFilter.MYPROJECTS ? project.insertedByUserId === props.Login.userid : project.isActive))}
            />
          </Grid2>
        )}
        {tableViewMode === TableView.GRID && <BackToTopButton />}
      </Box>
    </Box>
  );
}

export default ProjectReportOverviewPage;

const useStyles = makeStyles((theme) => ({

  loadingChart: {
    height: "100vh",
    width: "100%",
    top: "0px",
    position: "fixed",
    zIndex: "999",
    borderRadius: "8px",
    backgroundColor: "rgba(156,156,156,0.5)",
    display: "flex",
    alignContent: "center",
    alignItems: "center",
    justifyContent: "center",
  },

  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    flexDirection: "row",
    overflowX: "hidden",
  },

  header__right: {
    display: "flex",
    gap: "16px",
    alignItems: "center",
  },

  subscriptionInformationContainer: {
    display: "flex",
    gap: "8px",
    alignItems: "center",
  },

  subscriptionProjectCountChip: {
    height: "24px",
    padding: "4px 8px 4px 6px",

    display: "flex",
    alignItems: "center",
    gap: "4px",

    border: "1px solid #DCDCDC",
    borderRadius: "12px",
    backgroundColor: "#EDEDED",
  },

  chip__icons: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },

  chip__text: {
    display: "flex",
    alignItems: "center",
    gap: "2px",

    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontSize: "16px",
  },

  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",

    flexWrap: "wrap",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid var(--color-primary)",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },

  toggleFilter: {
    height: "32px",
    boxSizing: "border-box",

    position: "relative",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",
  },

  toggleFilter__optionButton: {
    height: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
  },

  toggleFilter__icon: {
    height: "16px",
    width: "16px",
    display: "block",

    color: "#0C0E29",

    strokeWidth: "1.4px",
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(50% - 1px)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  tableManagementContainer__primaryIconButton: {
    height: "32px",
    padding: "0 12px",
    paddingLeft: "6px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    display: "flex",
    gap: "4px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },


  primaryIconButton: {
    height: "40px",
    padding: "0 16px",
    paddingLeft: "8px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    display: "flex",
    gap: "8px",
    alignItems: "center",

    transition: "all 0.2s ease-in-out",

    "&:hover": {
      backgroundColor: "#a77d4f",
      color: "white",
      cursor: "pointer",
    },
  },

  iconButton__icon: {
    height: "20px",
    width: "20px",

    strokeWidth: "1.6px",

    display: "block",
  },

  buttonContainer: {
    display: "flex",
    alignItems: "center",
    alignContent: "center",
    flexDirection: "row",
  },

  openDropdownButton: {
    width: "48px",
    height: "48px",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",

    borderRadius: "4px",
    border: "none",

    backgroundColor: "transparent",
    color: "#0C0E29",

    padding: "0px",
    margin: "0px",

    transition: "background-color 0.1s",

    "&:hover": {
      cursor: "pointer",
      backgroundColor: "#EDEDED !important",
    },
  },

  row__dropdown: {
    position: "absolute",
    right: "16px",
    top: "68px",

    width: "fit-content",
    height: "fit-content",

    padding: "4px",

    backgroundColor: "white",
    zIndex: "999",
    boxShadow: "0px 3px 15px rgba(175, 175, 175, 0.25)",
    borderRadius: "6px",
  },

  row__dropdownButton: {
    width: "100%",
    height: "36px",
    borderRadius: "4px",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "medium",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",

    padding: "8px 12px",
    margin: "0px",

    color: "#0C0E29",

    transition: "background-color 0.1s",

    "&:hover": {
      backgroundColor: "#F8F8F8",
    },
  },

  dateContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    flexDirection: "row",
  },

  dataContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    flexDirection: "row",
  },

  dataContainer__label: {
    minWidth: "142px",

    color: "rgba(12, 14, 41, 0.8)",

    margin: "0px",
  },

  bar: {
    width: "100%",
  },

  boxStyle: {
    position: "relative",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    background: "#FFFFFF",
    borderRadius: "8px",
    padding: "16px",
    gap: "8px",
  },

  headerTitle: {
    // if name is too long, ellipsis
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  infinityIcon: {
    width: "16px",
    height: "16px",
    color: "#0C0E29",
  },
}));